<script setup lang="ts">
import type { PipedriveUser } from '@models/auth/pipedriveUser';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import ProfilePicture from '@components/ProfilePicture.vue';
import Typography from '@components/Typography.vue';
import { trans, transchoice } from '@elements/translation';
import { type Url } from '@global-types/url';

const props = defineProps<{
  accountManager: PipedriveUser | null;
}>();

const {
  isMobileOrTablet,
} = storeToRefs(useScreensizeStore());

const cdnUrl = computed<Url>(() => window.CDN_URL);
const companyPhone = computed<string>(() => window.COMPANY_PHONE);
const contactMail = computed<string>(() => window.CONTACT_MAIL);

const pictureUrl = computed<Url>(() => props.accountManager?.picture_url ?? `${cdnUrl.value}/images/company-avatar.svg`);

const title = computed<string>(() => (props.accountManager
  ? transchoice('user_menu.account_manager.contact', { account_manager: props.accountManager.firstname })
  : trans('user_menu.account_manager.unqualified')));

const phoneNumber = computed<string>(() => (props.accountManager
  ? props.accountManager.phone_number
  : companyPhone.value));

const email = computed<string>(() => props.accountManager?.email ?? contactMail.value);
</script>

<template>
  <div class="account-manager-menu">
    <ProfilePicture :url="pictureUrl" class="account-manager-menu__photo" />
    <div class="account-manager-menu__infos">
      <Typography :variant="isMobileOrTablet ? 'content-heading-2' : 'body-small-bold'">
        {{ title }}
      </Typography>
      <div class="account-manager-menu__links">
        <a v-if="phoneNumber" :href="`tel:${phoneNumber?.replace(/ /g, '')}`">
          <Typography :variant="isMobileOrTablet ? 'body' : 'body-small'">{{ phoneNumber }}</Typography>
        </a>
        <a :href="`mailto:${email}`">
          <Typography :variant="isMobileOrTablet ? 'body' : 'body-small'">{{ email }}</Typography>
        </a>
      </div>
    </div>
  </div>
</template>
